import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { fetchPatientDetailsUuid, postPatientReport } from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import FigmaHeader from './FigmaHeader/FigmaHeader';

// Common styles
const commonStyles = {
  input: {
    border: '1px solid #b0a4a4ff',
    outline: 'none',
    padding: '10px',
    backgroundColor: 'white',
    width: '100%',
    fontSize: '14px'
  },
  label: {
    fontWeight: 600,
    fontSize: '14px'
  },
  separator: {
    fontWeight: 600,
    fontSize: '14px'
  },
  section: {
    maxWidth: '97%',
    margin: '0 auto'
  }
};

// Section header component
const SectionHeader = ({title, className = 'spermiogram-head', inline = false}) => (
  <h2
    style={{
      textAlign: 'center',
      backgroundColor: '#f7f7f7',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: '16px',
      fontWeight: '600',
      padding: '5px 0 5px 10px',
      margin: inline ? '0' : '8px 0'
    }}
    className={className}>
    {title}
  </h2>
);

// Reusable FormField component
const FormField = ({label, type = 'text', name, value, onChange, show, options = [], width = '200px', labelWidth = '160px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  if (show) {
    // Display mode (read-only)
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${labelWidth} 16px 1fr`,
          alignItems: 'center',
          gap: '8px'
        }}>
        <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
        <span style={commonStyles.separator}>:</span>
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value}</span>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${labelWidth} 16px ${width}`,
        alignItems: 'center',
        gap: '8px'
      }}>
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
      <span style={commonStyles.separator}>:</span>

      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={inputStyle}>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
      )}
    </div>
  );
};

// Patient Info Section component
const PatientInfoSection = ({patientNumber, partnerType, show, handleChange, valueChange}) => {
  const genderOptions = [
    {value: '', label: 'Select Gender'},
    {value: 'Male', label: 'Male'},
    {value: 'Female', label: 'Female'},
    {value: 'Others', label: 'Others'},
    {value: 'Decline to State', label: 'Decline to State'}
  ];

  return (
    <div className='' style={{width: '100%', display: 'block'}} id={`patientInfo${patientNumber}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '3px',
          backgroundColor: '#f7f7f7 ',
          borderRadius: '6px',
          padding: '8px 4px 8px 4px',
          gap: '3px'
        }}>
        {/* Patient ID */}
        <FormField label='Patient ID' name={`patientId${patientNumber}`} value={valueChange[`patientId${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />

        {/* Patient Name */}
        <FormField
          label={`${partnerType} Partner Name`}
          name={`patientName${patientNumber}`}
          value={valueChange[`patientName${patientNumber}`]}
          onChange={handleChange}
          show={show}
          width='250px'
          labelWidth='125px'
        />

        {/* Gender */}
        <FormField
          label='Gender'
          type='select'
          name={`gender${patientNumber}`}
          value={valueChange[`gender${patientNumber}`]}
          onChange={handleChange}
          show={show}
          options={genderOptions}
          width='250px'
          labelWidth='125px'
        />

        {/* Age */}
        <FormField label='Age' name={`age${patientNumber}`} value={valueChange[`age${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
      </div>
    </div>
  );
};

function SemenAnalysis(props) {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    gender1: 'Male',
    gender2: 'Female',

    // 🔬 SAMPLE DETAILS
    sampleCollectionSite: '',
    modeOfCollection: 'Ejaculate',
    timeOfCollection: '',
    timeOfCollectionHour: '',
    timeOfCollectionMinute: '',
    timeOfCollectionPeriod: 'AM',
    timeOfExaminationHour: '',
    timeOfExaminationMinute: '',
    timeOfExaminationPeriod: 'AM',
    abstinencePeriod: '',
    spillage: '',
    otherSite: '',

    // ⚗️ MACROSCOPIC EXAMINATION
    liquefactionTime: '',
    appearance: '',
    volume: '',
    pH: '',
    viscosity: '',

    // 🔬 MICROSCOPIC EXAMINATION
    spermConcentration: '',
    totalSpermCount: '',
    totalMotility: '',
    progressiveMotility: '',
    nonProgressiveMotility: '',
    immotileSperm: '',
    vitality: '',
    agglutination: '',
    roundCells: '',
    otherCells: '',

    // 🧫 MORPHOLOGY (STRICT CRITERIA – WHO 2021)
    normalForms: '',
    abnormalForms: '',
    headAbnormalities: '',
    midpieceAbnormalities: '',
    tailAbnormalities: '',
    cytoplasmicDroplets: '',

    // 🩺 IMPRESSION
    impression: '',

    // Technician selection
    technician: '',
    technicianOtherName: '',

    // Clinician signature
    clinicianSignature: ''
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  // Handle different input types
  const handleChange = e => {
    const text = e.target.value;

    // Handle patient ID fetching
    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (text.length === 10 && text.charAt(0) === 'A') {
        const fetchData = async () => {
          const payload = {p_uhid: text};
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            const isSinglePatient = response.payload.data.data.length === 1;
            if (isSinglePatient) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;

              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName1: namePat1,
                  age1: data?.age,
                  gender1: data?.gender,
                  patientId1: text
                });
              } else {
                setPatientTwo_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName2: namePat1,
                  age2: data?.age,
                  gender2: data?.gender,
                  patientId2: text
                });
              }
            } else {
              const data = response.payload.data.data[0].gender === 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender === 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;

              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({
                ...valueChange,
                patientName1: namePat1,
                patientName2: namePat2,
                age1: data?.age,
                age2: data2?.age,
                gender1: data?.gender,
                gender2: data2?.gender,
                patientId1: data?.p_uhid,
                patientId2: data2?.p_uhid
              });
            }
            props.loaderchange('false');
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
        };
        fetchData();
      } else {
        setvalueChange({...valueChange, [e.target.name]: text});
      }
    }
    // Handle regular inputs
    else {
      setvalueChange({...valueChange, [e.target.name]: text});
    }
  };

  const handletest = async () => {
    try {
      if (
        valueChange.patientOne_UUId !== '' &&
        valueChange.patientId1 !== '' &&
        valueChange.patientId1.length === 10 &&
        (valueChange.patientId2 !== '' ? valueChange.patientId1 !== valueChange.patientId2 : true)
      ) {
        setshow(true);
        setTimeout(async () => {
          const htmlPayload = document.getElementById('reportcreate').innerHTML;
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'Sperm Report'
          };

          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');
            Swal.fire({
              title: 'Successful.',
              text: 'Report added successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
          } else {
            setshow(false);
            props.loaderchange(false);
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert(false);
            }, 2000);
          }
        }, 3000);
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const sideactive = useSelector(state => state.counter.sidebarnav);

  // Sample collection site options
  const collectionSiteOptions = [
    {value: '', label: 'Select Collection Site'},
    {value: 'Lab', label: 'Lab'},
    {value: 'Home', label: 'Home'},
    {value: 'Other', label: 'Other'}
  ];

  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>
                  Dashboard /
                </a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>
                  &nbsp;Patient Reports
                </a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        <h1 style={{fontSize: '18px'}}>SEMEN ANALYSIS REPORT</h1>
        <div style={{backgroundColor: 'white', maxWidth: '100%'}} className='patientreport' id='reportcreate'>
          {/* Patient Information Header */}
          {/* Component-level print styles for single-page fit */}

          <FigmaHeader />

          {/* Patient & Couple Details */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '3px',
              border: '1px solid #000',
              marginBottom: '5px'
            }}
            className='report-template-wrap1'>
            <PatientInfoSection patientNumber={1} partnerType='Male' show={show} handleChange={handleChange} valueChange={valueChange} />
            <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
              <PatientInfoSection patientNumber={2} partnerType='Female' show={show} handleChange={handleChange} valueChange={valueChange} />
            </div>
          </div>
          <h3 style={{textAlign: 'center', fontWeight: '600', backgroundColor: '#f7f7f7', padding: '0px 10px', fontSize: '16px'}}>SEMEN ANALYSIS REPORT</h3>

          <div style={{padding: '0px 10px'}}>
            {/* 🔬 SAMPLE DETAILS */}

            <SectionHeader title='🔬 SAMPLE DETAILS' />
            <div style={{marginBottom: '10px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '4px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '4px', border: '1px solid #ddd', textAlign: 'center'}}>Observation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Sample Collection Site</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'center'}}>
                          <select name='sampleCollectionSite' value={valueChange.sampleCollectionSite} onChange={handleChange} style={{...commonStyles.input, width: '120px'}}>
                            <option value=''>Select Site</option>
                            <option value='Lab'>Lab</option>
                            <option value='Home'>Home</option>
                            <option value='Other'>Other</option>
                          </select>
                          {valueChange.sampleCollectionSite === 'Other' && (
                            <input
                              type='text'
                              name='otherSite'
                              placeholder='Specify other site'
                              value={valueChange.otherSite}
                              onChange={handleChange}
                              style={{...commonStyles.input, width: '150px'}}
                            />
                          )}
                        </div>
                      ) : (
                        <span style={{fontSize: '14px'}}>
                          {valueChange.sampleCollectionSite}
                          {valueChange.sampleCollectionSite === 'Other' && valueChange.otherSite ? `: ${valueChange.otherSite}` : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Mode of Collection</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <input type='text' name='modeOfCollection' value={valueChange.modeOfCollection} onChange={handleChange} style={{...commonStyles.input, width: '120px'}} />
                      ) : (
                        <span style={{fontSize: '14px'}}>{valueChange.modeOfCollection}</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Time of Collection</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center'}}>
                          <input
                            type='number'
                            min='1'
                            max='12'
                            placeholder='hh'
                            name='timeOfCollectionHour'
                            value={valueChange.timeOfCollectionHour || ''}
                            onChange={handleChange}
                            style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                          />
                          <span>:</span>
                          <input
                            type='number'
                            min='0'
                            max='59'
                            placeholder='mm'
                            name='timeOfCollectionMinute'
                            value={valueChange.timeOfCollectionMinute || ''}
                            onChange={handleChange}
                            style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                          />
                          <select name='timeOfCollectionPeriod' value={valueChange.timeOfCollectionPeriod || ''} onChange={handleChange} style={{...commonStyles.input, width: '60px'}}>
                            <option value='AM'>AM</option>
                            <option value='PM'>PM</option>
                          </select>
                        </div>
                      ) : (
                        <span style={{fontSize: '14px'}}>
                          {valueChange.timeOfCollectionHour && valueChange.timeOfCollectionMinute && valueChange.timeOfCollectionPeriod
                            ? `${valueChange.timeOfCollectionHour}:${valueChange.timeOfCollectionMinute.toString().padStart(2, '0')} ${valueChange.timeOfCollectionPeriod}`
                            : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Time of Examination</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center'}}>
                          <input
                            type='number'
                            min='1'
                            max='12'
                            placeholder='hh'
                            name='timeOfExaminationHour'
                            value={valueChange.timeOfExaminationHour || ''}
                            onChange={handleChange}
                            style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                          />
                          <span>:</span>
                          <input
                            type='number'
                            min='0'
                            max='59'
                            placeholder='mm'
                            name='timeOfExaminationMinute'
                            value={valueChange.timeOfExaminationMinute || ''}
                            onChange={handleChange}
                            style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                          />
                          <select name='timeOfExaminationPeriod' value={valueChange.timeOfExaminationPeriod || ''} onChange={handleChange} style={{...commonStyles.input, width: '60px'}}>
                            <option value='AM'>AM</option>
                            <option value='PM'>PM</option>
                          </select>
                        </div>
                      ) : (
                        <span>
                          {valueChange.timeOfExaminationHour && valueChange.timeOfExaminationMinute && valueChange.timeOfExaminationPeriod
                            ? `${valueChange.timeOfExaminationHour}:${valueChange.timeOfExaminationMinute.toString().padStart(2, '0')} ${valueChange.timeOfExaminationPeriod}`
                            : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Abstinence Period</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <input
                            type='number'
                            name='abstinencePeriod'
                            value={valueChange.abstinencePeriod}
                            onChange={handleChange}
                            style={{...commonStyles.input, width: '120px'}}
                            placeholder='Days'
                          />
                        </div>
                      ) : (
                        <span>{valueChange.abstinencePeriod} Days</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Spillage</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <select name='spillage' value={valueChange.spillage} onChange={handleChange} style={{...commonStyles.input, width: '120px'}}>
                          <option value=''>Select</option>
                          <option value='None'>None</option>
                          <option value='Partial'>Partial</option>
                          <option value='Final'>Final</option>
                        </select>
                      ) : (
                        <span>{valueChange.spillage}</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ⚗️ MACROSCOPIC EXAMINATION */}
            <SectionHeader title='⚗️ MACROSCOPIC EXAMINATION' />
            <div style={{marginBottom: '20px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Result</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>WHO Reference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Time of Liquefaction</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='number'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='liquefactionTime'
                        value={valueChange.liquefactionTime || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.liquefactionTime ? `${valueChange.liquefactionTime}` : ''}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>mins</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Appearance</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='appearance'
                        value={valueChange.appearance || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.appearance}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Opalescent, grey-white</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Volume</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='volume'
                        value={valueChange.volume || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.volume ? `${valueChange.volume} ` : ''}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=1.4ml`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>pH</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='pH'
                        value={valueChange.pH || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.pH}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=7.2`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Viscosity</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='viscosity'
                        value={valueChange.viscosity || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.viscosity}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 🔬 MICROSCOPIC EXAMINATION */}
            <SectionHeader title='🔬 MICROSCOPIC EXAMINATION' />
            <div style={{marginBottom: '20px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Result</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>WHO Reference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Sperm Concentration</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='spermConcentration'
                        value={valueChange.spermConcentration || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.spermConcentration}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=16 millions`}</td>
                  </tr>

                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Total Motility</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='totalMotility'
                        value={valueChange.totalMotility || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalMotility}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=42%`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Progressive Motility</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='progressiveMotility'
                        value={valueChange.progressiveMotility || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.progressiveMotility}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>= 30%`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Non-Progressive Motility</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='nonProgressiveMotility'
                        value={valueChange.nonProgressiveMotility || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.nonProgressiveMotility}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>1%</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Immotile Sperm</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='immotileSperm'
                        value={valueChange.immotileSperm || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.immotileSperm}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>20%</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Total Sperm Count (per ejaculate)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='totalSpermCount'
                        value={valueChange.totalSpermCount || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalSpermCount}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>39 millions</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Vitality(if done)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='vitality'
                        value={valueChange.vitality || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.vitality}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>= 54%`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Agglutination</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='agglutination'
                        value={valueChange.agglutination || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.agglutination}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Round Cells / HPF</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='roundCells'
                        value={valueChange.roundCells || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.roundCells}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Other Cells / Debris</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='otherCells'
                        value={valueChange.otherCells || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.otherCells}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 🧫 MORPHOLOGY (STRICT CRITERIA – WHO 2021) */}
            <SectionHeader title='🧫 MORPHOLOGY (STRICT CRITERIA – WHO 2021)' />
            <div style={{marginBottom: '20px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Normal Forms</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <input type='text' name='normalForms' value={valueChange.normalForms || ''} onChange={e => handleChange(e)} style={{...commonStyles.input, width: '150px'}} />
                          <span style={{marginLeft: '5px'}}>%</span>
                        </div>
                      ) : (
                        <span>{`${valueChange.normalForms}%`}</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Abnormal Forms</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <input type='text' name='abnormalForms' value={valueChange.abnormalForms || ''} onChange={e => handleChange(e)} style={{...commonStyles.input, width: '150px'}} />
                          <span style={{marginLeft: '5px'}}>%</span>
                        </div>
                      ) : (
                        <span>{`${valueChange.abnormalForms}%`}</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Type of Abnormalities</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      {!show ? (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center'}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{width: '180px', textAlign: 'left'}}>Head:</span>
                            <input type='text' name='headAbnormalities' value={valueChange.headAbnormalities || ''} onChange={handleChange} style={{...commonStyles.input, width: '150px'}} />
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{width: '180px', textAlign: 'left'}}>Midpiece:</span>
                            <input type='text' name='midpieceAbnormalities' value={valueChange.midpieceAbnormalities || ''} onChange={handleChange} style={{...commonStyles.input, width: '150px'}} />
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{width: '180px', textAlign: 'left'}}>Tail:</span>
                            <input type='text' name='tailAbnormalities' value={valueChange.tailAbnormalities || ''} onChange={handleChange} style={{...commonStyles.input, width: '150px'}} />
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{width: '180px', textAlign: 'left'}}>Cytoplasmic droplets:</span>
                            <input type='text' name='cytoplasmicDroplets' value={valueChange.cytoplasmicDroplets || ''} onChange={handleChange} style={{...commonStyles.input, width: '150px'}} />
                          </div>
                        </div>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center', justifyContent: 'center'}}>
                          <span>Head: {valueChange.headAbnormalities},</span>
                          <span>Midpiece: {valueChange.midpieceAbnormalities},</span>
                          <span>Tail: {valueChange.tailAbnormalities},</span>
                          <span>Cytoplasmic droplets: {valueChange.cytoplasmicDroplets}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 🩺 IMPRESSION */}
            <div style={{marginBottom: '10px'}}>
              <SectionHeader title='🩺 IMPRESSION' inline={true} />
              {!show ? (
                <textarea
                  name='impression'
                  value={valueChange.impression}
                  onChange={handleChange}
                  style={{
                    ...commonStyles.input,
                    width: '100%',
                    minHeight: '60px',
                    padding: '10px',
                    fontFamily: 'Arial, sans-serif',
                    marginTop: '5px'
                  }}
                  placeholder='Enter clinical impression and diagnosis...'
                />
              ) : (
                valueChange.impression && (
                  <div
                    style={{
                      border: '1px solid #ddd',
                      padding: '10px',
                      minHeight: '40px',
                      backgroundColor: '#f9f9f9',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'Arial, sans-serif',
                      marginTop: '5px'
                    }}>
                    {valueChange.impression}
                  </div>
                )
              )}
            </div>

            {/* 📊 WHO 2021 REFERENCE VALUES */}
            <SectionHeader title='📊 WHO 2021 REFERENCE VALUES (Quick Reference)' />
            <div style={{marginBottom: '10px', padding: '5px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6'}}>
              {!show ? (
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <tbody>
                    <tr>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>| Volume ≥ 1.4 ml |</td>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>Concentration ≥ 16 million/ml |</td>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>Total Count ≥ 39 million |</td>
                    </tr>
                    <tr>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>Motility ≥ 42% |</td>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>Progressive ≥ 30% |</td>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>Vitality ≥ 54% |</td>
                    </tr>
                    <tr>
                      <td style={{padding: '5px', fontWeight: 'bold', border: '1px solid #dee2e6'}}>Normal Forms ≥ 4% |</td>
                      <td style={{padding: '5px', fontWeight: 'bold'}}></td>
                      <td style={{padding: '5px', fontWeight: 'bold'}}></td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    justifyContent: 'flex-start',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>| Volume ≥ 1.4 ml |</span>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>Concentration ≥ 16 million/ml |</span>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>Total Count ≥ 39 million |</span>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>Motility ≥ 42% |</span>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>Progressive ≥ 30% |</span>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>Vitality ≥ 54% |</span>
                  <span style={{padding: '2px 6px', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '14px'}}>Normal Forms ≥ 4% |</span>
                </div>
              )}
            </div>

            {/* Technician and Clinician Signatures */}
            <div style={{marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div style={{display: 'flex', alignItems: 'center'}}>
                <strong style={{marginRight: '10px'}}>Technician: </strong>
                {!show ? (
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <select name='technician' value={valueChange.technician} onChange={handleChange} style={{...commonStyles.input, width: '200px'}}>
                      <option value=''>Select Technician</option>
                      <option value='Sebastian'>Sebastian</option>
                      <option value='Karthik'>Karthik</option>
                      <option value='Siva'>Siva</option>
                      <option value='Sangeetha'>Sangeetha</option>
                      <option value='Other'>Other</option>
                    </select>
                    {valueChange.technician === 'Other' && (
                      <input
                        type='text'
                        name='technicianOtherName'
                        placeholder='Specify other technician'
                        value={valueChange.technicianOtherName}
                        onChange={handleChange}
                        style={{...commonStyles.input, width: '200px', marginLeft: '10px'}}
                      />
                    )}
                  </div>
                ) : (
                  <span style={{marginLeft: '10px'}}>{valueChange.technician === 'Other' && valueChange.technicianOtherName ? valueChange.technicianOtherName : valueChange.technician}</span>
                )}
              </div>

              <div style={{display: 'flex', alignItems: 'center'}}>
                <strong style={{marginRight: '10px'}}>Clinician Signature:</strong>
                {!show ? (
                  <input
                    type='text'
                    name='clinicianSignature'
                    value={valueChange.clinicianSignature}
                    onChange={handleChange}
                    style={{
                      ...commonStyles.input,
                      width: '300px',
                      border: 'none',
                      borderBottom: '1px solid #000'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: 'inline-block',
                      minWidth: '300px',
                      borderBottom: '1px solid #000',
                      paddingBottom: '5px'
                    }}>
                    {valueChange.clinicianSignature}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
}

export default SemenAnalysis;
